import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useEffect } from "react";

const loginSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(1, "Password is required"),
});

type LoginFormValues = z.infer<typeof loginSchema>;

const Login = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  useEffect(() => {
    document.title = "Sign In | EMU Mentoring";
  }, []);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const onSubmit = async (data: LoginFormValues) => {
    setLoginError(null);
    setIsSubmitting(true);
    
    try {
      console.log("Login attempt with:", data.email);
      await handleRegularLogin(data);
    } catch (error: any) {
      console.error("Login error:", error);
      setLoginError("An unexpected error occurred. Please try again.");
      setIsSubmitting(false);
    }
  };


  const handleRegularLogin = async (data: LoginFormValues) => {
    try {
      const isLocked = await checkAccountLocked(data.email);
      if (isLocked) {
        setLoginError("Account locked due to too many failed login attempts. Please contact support or use the unlock button below.");
        setIsSubmitting(false);
        return;
      }

      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      });
      
      if (signInError) {
        console.error("Login error:", signInError);
        await incrementFailedAttempts(data.email);
        
        const isNowLocked = await checkAccountLocked(data.email);
        if (isNowLocked) {
          setLoginError("Account locked due to too many failed login attempts. Please contact support or use the unlock button below.");
        } else {
          setLoginError("Invalid email or password. Please try again.");
        }
        
        setIsSubmitting(false);
        return;
      }

      if (!signInData.user) {
        setLoginError("Error signing in: No user returned");
        setIsSubmitting(false);
        return;
      }

      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', signInData.user.id)
        .single();
      
      if (profileError) {
        console.error("Error fetching profile data:", profileError);
        setLoginError("Could not retrieve user profile. Please try again.");
        setIsSubmitting(false);
        return;
      }
      
      if (!profileData) {
        console.error("No profile found for user");
        setLoginError("No profile found for this account. Please contact support.");
        setIsSubmitting(false);
        return;
      }
      
      if (profileData.status === 'Pending') {
        setLoginError("Your account is still under review. We'll notify you once it's approved.");
        setIsSubmitting(false);
        return;
      }
      
      const userData = {
        id: profileData.id,
        email: profileData.email,
        firstName: profileData.first_name || '',
        lastName: profileData.last_name || '',
        profileType: profileData.profile_type || 'user',
        isAdmin: profileData.is_admin || false,
        status: profileData.status || 'Active',
        isApproved: profileData.is_approved || false
      };

      localStorage.setItem("userData", JSON.stringify(userData));
      
      setUser(userData);
      
      toast({
        title: "Login successful!",
        description: "Welcome back to EMU Mentoring!",
      });
      
      await resetFailedAttempts(data.email);
      
      setIsSubmitting(false);
      
      if (profileData.is_admin) {
        navigate("/admin");
      } else if (profileData.profile_type === "mentor") {
        navigate("/mentor-dashboard");
      } else if (profileData.profile_type === "mentee") {
        navigate("/mentee-dashboard");
      } else if (profileData.profile_type === "both") {
        navigate("/mentor-dashboard");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Login error:", error);
      setLoginError("An unexpected error occurred. Please try again.");
      setIsSubmitting(false);
    }
  };


  const checkAccountLocked = async (email: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from('account_locks')
        .select('locked')
        .eq('email', email)
        .single();
      
      if (error && error.code !== 'PGRST116') {
        console.error("Error checking account lock:", error);
      }
      
      return data?.locked || false;
    } catch (error) {
      console.error("Account lock check error:", error);
      return false;
    }
  };

  const incrementFailedAttempts = async (email: string) => {
    try {
      const { data, error } = await supabase
        .from('account_locks')
        .select('*')
        .eq('email', email)
        .single();
      
      if (error && error.code !== 'PGRST116') {
        console.error("Error checking account lock:", error);
        return;
      }
      
      if (!data) {
        await supabase
          .from('account_locks')
          .insert([{
            email,
            attempts: 1,
            locked: false,
            last_attempt: new Date().toISOString()
          }]);
      } else {
        const newAttempts = (data.attempts || 0) + 1;
        const shouldLock = newAttempts >= 5;
        
        await supabase
          .from('account_locks')
          .update({
            attempts: newAttempts,
            locked: shouldLock,
            last_attempt: new Date().toISOString()
          })
          .eq('email', email);
      }
    } catch (error) {
      console.error("Failed attempts tracking error:", error);
    }
  };

  const resetFailedAttempts = async (email: string) => {
    try {
      await supabase
        .from('account_locks')
        .update({ attempts: 0, locked: false })
        .eq('email', email);
    } catch (error) {
      console.error("Reset failed attempts error:", error);
    }
  };

  const handleUnlockAccount = async () => {
    const email = form.getValues().email;
    if (!email) {
      setLoginError("Please enter an email address to unlock.");
      return;
    }
    
    try {
      const { error } = await supabase
        .from('account_locks')
        .update({ locked: false, attempts: 0 })
        .eq('email', email);
      
      if (error) {
        console.error("Error unlocking account:", error);
        toast({
          title: "Error",
          description: "Failed to unlock the account. Please try again.",
          variant: "destructive",
        });
        return;
      }
      
      toast({
        title: "Account Unlocked",
        description: `${email} has been unlocked successfully. You can now try to log in.`,
        variant: "default",
      });
      setLoginError(null);
    } catch (error) {
      console.error("Error unlocking account:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred while unlocking the account.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2 overflow-hidden">
      {/* Left panel (logo) */}
      <div className="relative bg-[#003BA3] min-h-screen">
        <div className="absolute inset-0 flex items-center justify-center w-full h-full">
          {/* Use a fixed box so no reflow while the image loads */}
          <div className="w-[614px] h-[614px] max-w-none">
            <img
              src="/lovable-uploads/6345f7f9-e1bc-456f-b3b1-96690802a8b0.png"
              alt="EMU Logo"
              width={614}
              height={614}
              className="w-[614px] h-[614px] object-contain max-w-none"
              loading="eager"
            />
          </div>
        </div>
      </div>

      {/* Right panel (auth card) */}
      <div className="flex items-center justify-center p-4 md:p-10">
        <div className="w-full max-w-[520px]">
          <div className="mb-4">
            <div className="flex md:hidden justify-center mb-4">
              <img 
                src="/lovable-uploads/6345f7f9-e1bc-456f-b3b1-96690802a8b0.png" 
                alt="EMU Logo" 
                className="h-6 object-contain" 
              />
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-emu-blue mb-2">Sign In</h1>
            <div className="flex items-center text-sm text-gray-600">
              <span>or</span>
              <Link to="/sign-up" className="ml-1 text-emu-blue hover:underline">
                Join as a Mentor or Mentee
              </Link>
            </div>
          </div>

          {loginError && (
            <Alert variant="destructive" className="mb-4">
              <AlertTitle>Login failed</AlertTitle>
              <AlertDescription>{loginError}</AlertDescription>
            </Alert>
          )}

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="your@email.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <Input type="password" placeholder="••••••••" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex justify-end">
                <Link to="/forgot-password" className="text-sm text-emu-blue hover:underline">
                  Forgot password?
                </Link>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-emu-blue hover:bg-emu-darkblue text-white"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Signing in..." : "Sign In"}
              </Button>
              
              {loginError && loginError.includes("locked") && (
                <Button 
                  type="button"
                  variant="outline"
                  className="w-full mt-2"
                  onClick={handleUnlockAccount}
                >
                  Unlock Account
                </Button>
              )}
            </form>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default Login;
